import type { TableProps } from 'antd';
import { Empty } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AppButton, AppModal, AppSpin, AppTable } from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import { EMPTY_TEXT_FILTERED } from '@/constants/messages';
import { useCertificationList } from '@/hooks/main/usePatientCertification';
import useAppToast from '@/hooks/useAppToast';
import dayjs from '@/shared/lib/dayjs';
import { japaneseDateToISO } from '@/utils/dateConverter';

export interface CertificationItem {
  id: number;
  name: string;
  name_kana: string;
  birthdate: string;
  sex: string;
  mark: string;
  patient_id: number | null;
  qualification_date: string;
  qualification_confirmation_on: string;
  qualification_confirmation_on_YYYYMMDD: string;
}

interface CertificationListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (certificationId: number) => void;
}

type CertificationColumns = NonNullable<TableProps<CertificationItem>['columns']>;

const PAGE_SIZE = 20;

const columns: CertificationColumns = [
  {
    title: '項番',
    key: 'index',
    width: 60,
    render: (_, __, index) => index + 1,
  },
  {
    title: 'マーク',
    dataIndex: 'mark',
    key: 'mark',
    width: 60,
  },
  {
    title: '氏名',
    dataIndex: 'name',
    key: 'name',
    width: 150,
    align: 'left',
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: '氏名カナ',
    dataIndex: 'name_kana',
    key: 'name_kana',
    width: 150,
    align: 'left',
    sorter: (a, b) => (a.name_kana || '').localeCompare(b.name_kana || ''),
  },
  {
    title: '生年月日',
    dataIndex: 'birthdate',
    key: 'birthdate',
    width: 120,
  },
  {
    title: '性別',
    dataIndex: 'sex',
    key: 'sex',
    width: 60,
  },
  {
    title: '資格取得年月日',
    dataIndex: 'qualification_date',
    key: 'qualification_date',
    width: 130,
    sorter: (a, b) => (a.qualification_date || '').localeCompare(b.qualification_date || ''),
  },
  {
    title: '資格確認日',
    dataIndex: 'qualification_confirmation_on',
    key: 'qualification_confirmation_on',
    width: 130,
    sorter: (a, b) =>
      (a.qualification_confirmation_on_YYYYMMDD || '').localeCompare(
        b.qualification_confirmation_on_YYYYMMDD || ''
      ),
  },
];

const CertificationListModal: React.FC<CertificationListModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const toast = useAppToast();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [page, setPage] = useState(1);

  // BD 6.1 — Datepicker max = today (disable future).
  const todayIso = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  // Calculate default date range: 1st of current month to last day of next month
  const defaultDateRange = useMemo(() => {
    const now = dayjs();
    const currentMonthStart = now.startOf('month').format('YYYY-MM-DD');
    const nextMonthEnd = now.add(1, 'month').endOf('month').format('YYYY-MM-DD');
    return { currentMonthStart, nextMonthEnd };
  }, []);

  // `undefined` = no filter (initial load), object = filtered.
  const [filterParams, setFilterParams] = useState<
    { date_from?: string; date_to?: string } | undefined
  >(undefined);

  const {
    data: certifications = [],
    isLoading,
    isFetching,
  } = useCertificationList(filterParams, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedRowKeys([]);
    setStartDate('');
    setEndDate('');
    setDateError('');
    setFilterParams(undefined);
    setPage(1);
  }, [isOpen]);

  // Set default date range when modal opens (only once)
  useEffect(() => {
    if (!isOpen || startDate || endDate) return;
    setStartDate(defaultDateRange.currentMonthStart);
    setEndDate(defaultDateRange.nextMonthEnd);
  }, [isOpen, startDate, endDate, defaultDateRange]);

  // BD 6.1 — Default sort: 資格確認日 DESC, then 氏名カナ ASC.
  const sortedData = useMemo(() => {
    return [...certifications].sort((a, b) => {
      const dateA = a.qualification_confirmation_on_YYYYMMDD || '';
      const dateB = b.qualification_confirmation_on_YYYYMMDD || '';
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return (a.name_kana || '').localeCompare(b.name_kana || '');
    });
  }, [certifications]);

  const total = sortedData.length;
  const showPagination = total > PAGE_SIZE;

  const pagedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const start = (page - 1) * PAGE_SIZE;
    return sortedData.slice(start, start + PAGE_SIZE);
  }, [sortedData, page, showPagination]);

  const handleFilter = useCallback(() => {
    // BD: both empty → use default range; exactly one filled → error; both filled → filter.
    if (!startDate && !endDate) {
      setDateError('');
      setSelectedRowKeys([]);
      setFilterParams({
        date_from: defaultDateRange.currentMonthStart,
        date_to: defaultDateRange.nextMonthEnd,
      });
      setPage(1);
      return;
    }

    if ((startDate && !endDate) || (!startDate && endDate)) {
      setDateError('開始日と終了日は両方入力してください');
      return;
    }

    const dateFrom = japaneseDateToISO(startDate);
    const dateTo = japaneseDateToISO(endDate);

    if (!dateFrom) {
      setDateError('開始日の形式が正しくありません');
      return;
    }
    if (!dateTo) {
      setDateError('終了日の形式が正しくありません');
      return;
    }
    if (dateFrom > dateTo) {
      setDateError(`終了日を${startDate}以降にしてください`);
      return;
    }

    setDateError('');
    setSelectedRowKeys([]);
    setFilterParams({ date_from: dateFrom, date_to: dateTo });
    setPage(1);
  }, [startDate, endDate]);

  // Reset to default date range when reset button clicked
  const handleResetDates = useCallback(() => {
    setStartDate(defaultDateRange.currentMonthStart);
    setEndDate(defaultDateRange.nextMonthEnd);
    setDateError('');
    setSelectedRowKeys([]);
    setFilterParams({
      date_from: defaultDateRange.currentMonthStart,
      date_to: defaultDateRange.nextMonthEnd,
    });
    setPage(1);
  }, [defaultDateRange]);

  const handleApply = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      toast.warning('資格情報を選択してください');
      return;
    }
    onSelect(selectedRowKeys[0] as number);
    onClose();
  }, [selectedRowKeys, onSelect, onClose, toast]);

  const handleRowDoubleClick = useCallback(
    (record: CertificationItem) => {
      onSelect(record.id);
      onClose();
    },
    [onSelect, onClose]
  );

  if (!isOpen) return null;

  const showLoading = isLoading || isFetching;
  const selectedId = selectedRowKeys[0];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="資格情報一覧"
      footer={null}
      maxWidth="max-w-5xl"
    >
      <div className="flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-end gap-2 mb-4">
          <span className="text-sm">資格確認日</span>
          <RA2NDatePicker
            value={startDate}
            onChange={setStartDate}
            placeholder="元号00年00月00日"
            className="w-40"
            endDate={todayIso}
          />
          <span>～</span>
          <RA2NDatePicker
            value={endDate}
            onChange={setEndDate}
            placeholder="元号00年00月00日"
            className="w-40"
            endDate={todayIso}
          />
          <AppButton type="primary" onClick={handleFilter} loading={showLoading}>
            絞り込み
          </AppButton>
          <AppButton onClick={handleResetDates}>リセット</AppButton>
        </div>

        {dateError && <div className="mb-2 text-sm text-right text-red-500">{dateError}</div>}

        <div className="relative flex flex-col flex-1 mb-4">
          {showLoading ? (
            <div className="flex items-center justify-center h-full py-10">
              <AppSpin size="large" />
            </div>
          ) : (
            <AppTable<CertificationItem>
              columns={columns}
              dataSource={pagedData.map(cert => ({ ...cert, key: cert.id }))}
              pagination={
                showPagination
                  ? {
                      current: page,
                      pageSize: PAGE_SIZE,
                      total,
                      showSizeChanger: false,
                      showTotal: (t: number) => `${t}件`,
                      onChange: setPage,
                    }
                  : false
              }
              bordered={false}
              rowClassName={record =>
                `cursor-pointer ${record.id === selectedId ? 'cert-row-selected' : ''}`
              }
              onRow={record => ({
                onClick: () => setSelectedRowKeys([record.id]),
                onDoubleClick: () => handleRowDoubleClick(record),
              })}
              scroll={{ y: 400 }}
              recordCount={sortedData.length}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      filterParams ? EMPTY_TEXT_FILTERED : 'データが登録されていません。'
                    }
                  />
                ),
              }}
            />
          )}
        </div>

        <div className="mb-4 text-sm text-gray-600">※「マーク」欄　〇：既存保険証の可能性あり</div>

        <div className="flex justify-end pt-4 border-t">
          <AppButton type="primary" onClick={handleApply} disabled={selectedRowKeys.length === 0}>
            保険証読取画面に反映
          </AppButton>
        </div>
      </div>
    </AppModal>
  );
};

export default CertificationListModal;
